// v2.71.6 — Phase 6: URL state sync for property filters
//
// Serialises the Dashboard `filters` object into a compact URL query string
// (so users can share search links, use browser back/forward to undo, and
// the page can re-hydrate on reload). Mirrors `defaultFilters()` shape.

// Keys we'll mirror in the URL. Any key not listed is ignored.
const SUPPORTED_KEYS = [
  "listing_intent", "listing_type", "property_type",
  "main_area", "sub_area", "area",
  "bhk", "tag",
  // v2.85 Phase B — Posted-By filter (Owner/Dealer/Builder).
  "posted_by",
];

const PRICE_KEY = "priceRange";  // [min, max]
const METRO_KEY = "maxMetro";    // single int
const PAGE_KEY = "page";         // v2.75 — current loaded page depth (1-based)

/**
 * Build a `URLSearchParams` string from current filter state.
 * Skips keys that match the default values so URLs stay clean.
 *
 * @param {Object}  filters   Current filter state (Dashboard-shaped).
 * @param {Object}  defaults  defaultFilters() output.
 * @param {Object}  [extras]  Optional extras: { page } — v2.75 page depth.
 */
export function filtersToQuery(filters, defaults, extras = null) {
  const p = new URLSearchParams();
  if (!filters) return "";

  for (const k of SUPPORTED_KEYS) {
    const v = filters[k];
    if (v && v !== defaults[k]) p.set(k, String(v));
  }

  // Custom filters (arbitrary admin-defined keys with string values)
  for (const [k, v] of Object.entries(filters)) {
    if (SUPPORTED_KEYS.includes(k)) continue;
    if (k === PRICE_KEY || k === METRO_KEY) continue;
    if (v === "" || v === null || v === undefined) continue;
    if (Array.isArray(v)) continue; // arrays are handled below
    // Only mirror primitives that differ from default
    if (String(v) !== String(defaults?.[k] ?? "")) p.set(k, String(v));
  }

  // Price range — only set if different from defaults
  const pr = filters[PRICE_KEY];
  const dpr = defaults[PRICE_KEY] || [];
  if (pr && Array.isArray(pr) && (pr[0] !== dpr[0] || pr[1] !== dpr[1])) {
    if (pr[0] !== dpr[0]) p.set("min_price", String(pr[0]));
    if (pr[1] !== dpr[1]) p.set("max_price", String(pr[1]));
  }

  // Max metro distance
  if (filters[METRO_KEY] !== undefined && filters[METRO_KEY] !== defaults[METRO_KEY]) {
    p.set("metro", String(filters[METRO_KEY]));
  }

  // v2.75 — Page depth (only if > 1). Used for browser back-nav restoration.
  if (extras && extras.page && Number(extras.page) > 1) {
    p.set(PAGE_KEY, String(extras.page));
  }

  return p.toString();
}

/**
 * Re-hydrate filter state from a URL query string. Falls back to `defaults`
 * for any key not present in the URL.
 *
 * v2.75 — also returns the parsed `page` value (defaults to 1) so callers
 * can pass it to the paginated-list hook for state reconstruction.
 */
export function queryToFilters(search, defaults) {
  const p = new URLSearchParams(search || "");
  const f = { ...defaults };

  for (const k of SUPPORTED_KEYS) {
    if (p.has(k)) f[k] = p.get(k);
  }

  // Custom filter keys mirrored back
  for (const [k, v] of p.entries()) {
    if (SUPPORTED_KEYS.includes(k)) continue;
    if (k === "min_price" || k === "max_price" || k === "metro" || k === PAGE_KEY) continue;
    f[k] = v;
  }

  // Price range
  const minP = p.get("min_price");
  const maxP = p.get("max_price");
  if (minP || maxP) {
    const dpr = defaults[PRICE_KEY] || [0, 0];
    f[PRICE_KEY] = [minP ? Number(minP) : dpr[0], maxP ? Number(maxP) : dpr[1]];
  }

  // Max metro
  const m = p.get("metro");
  if (m !== null) f[METRO_KEY] = Number(m);

  return f;
}

/**
 * v2.75 — Parse the page-depth param from a URL query string. Used by
 * Dashboard to reconstruct prior browsing depth on browser Back / Refresh.
 * Returns an int >= 1.
 */
export function queryToPage(search) {
  const p = new URLSearchParams(search || "");
  const n = parseInt(p.get(PAGE_KEY) || "1", 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

/**
 * Build a stable short label for a saved search based on the filters that
 * differ from defaults. e.g. "2BHK · Dwarka · For Rent · under ₹50L".
 */
export function describeFilters(filters, defaults) {
  const parts = [];
  if (filters.bhk) parts.push(filters.bhk);
  if (filters.main_area) parts.push(filters.main_area);
  if (filters.sub_area) parts.push(filters.sub_area);
  if (filters.property_type) parts.push(filters.property_type);
  if (filters.listing_intent === "sale") parts.push("For Sale");
  else if (filters.listing_intent === "rent") parts.push("For Rent");
  else if (filters.listing_intent === "lease") parts.push("For Lease");
  const pr = filters.priceRange;
  const dpr = defaults?.priceRange || [];
  if (pr && Array.isArray(pr) && (pr[0] !== dpr[0] || pr[1] !== dpr[1])) {
    const fmt = (n) => n >= 10000000 ? `${(n / 10000000).toFixed(1).replace(/\.0$/, "")}Cr` : n >= 100000 ? `${Math.round(n / 100000)}L` : n;
    parts.push(`₹${fmt(pr[0])}–₹${fmt(pr[1])}`);
  }
  return parts.length ? parts.join(" · ") : "All listings";
}
